import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApolloQueryResult } from '@apollo/client';

import {
  useListNamespacesQuery,
  type ListNamespacesQuery,
} from 'src/generated/graphql';

/**
 * Namespaces shown per pager page.
 *
 * Purely a display concern: pages are sliced client-side out of the
 * accumulated array, never fetched one-per-page.
 */
export const NAMESPACE_UI_PAGE_SIZE = 25;

/**
 * Namespaces requested per backend call.
 *
 * Deliberately larger than {@link NAMESPACE_UI_PAGE_SIZE} — a single round-trip
 * feeds several pager pages. Fetch size and page size are separate concerns.
 */
export const NAMESPACE_FETCH_BATCH_SIZE = 100;

/**
 * Batches fetched up-front on mount and after every filter change
 * (NAMESPACE_INITIAL_BATCHES × NAMESPACE_FETCH_BATCH_SIZE = 500 namespaces).
 *
 * A MOUNT-TIME PREFETCH BOUND ONLY — deliberately bounded because the namespace
 * list has no upper limit, so draining every page on mount would not scale. It
 * does NOT cap how far the list can grow: navigating past the loaded frontier
 * extends it on demand (see `goToPage`), so the reachable range is unbounded.
 */
export const NAMESPACE_INITIAL_BATCHES = 5;

export interface UseNamespaceListResult {
  /** Namespace ids for the current UI page (client-side slice). */
  namespaceIds: string[];
  currentPage: number;
  /** Number of pages the namespaces loaded so far fill. */
  pagesCount: number;
  /** True when the server still holds namespaces beyond the ones loaded. */
  hasMore: boolean;
  loading: boolean;
  /** True while an on-demand extension request is in flight. */
  isLoadingMore: boolean;
  error?: Error;
  /** Count of namespaces on the current page (0 when empty). */
  totalLoaded: number;
  goToPage: (page: number) => Promise<void>;
}

/**
 * Loads namespaces in batches into local state, then paginates over that array
 * client-side.
 *
 * On mount — and again after every filter change — the hook auto-chains
 * {@link NAMESPACE_INITIAL_BATCHES} requests of {@link NAMESPACE_FETCH_BATCH_SIZE}
 * namespaces each, accumulating every response into one array. The pager then
 * slices that array, so moving between pages issues no network request at all.
 *
 * Once that prefetch settles, the list grows EXTEND-ON-DEMAND: navigating past
 * the last loaded page while the server still holds a cursor follows that
 * cursor — and any further ones — until the requested page actually exists,
 * then lands on it. The prefetch bound therefore governs only the up-front
 * cost, never the reachable range.
 *
 * The Apollo cache is bypassed entirely (`fetchPolicy: 'no-cache'`): the loaded
 * namespaces live in this hook's state and must always come fresh from the
 * server.
 *
 * Prefix search stays SERVER-side (`namespaceIdPrefix`). This is a deliberate
 * divergence from `useStringGroupsData`, which filters its loaded items
 * client-side: the namespace list has no upper bound, so local-only filtering
 * would mean pulling every namespace first.
 */
export function useNamespaceList(searchFilter: string): UseNamespaceListResult {
  // --- Loaded data state ---
  const [allNamespaceIds, setAllNamespaceIds] = useState<string[]>([]);
  const [browseToken, setBrowseToken] = useState<string | null>(null);
  const [browseInitialized, setBrowseInitialized] = useState(false);

  // --- Auto-chain state ---
  /** How many batches have been loaded so far (1 = initial query result). */
  const [batchesFetched, setBatchesFetched] = useState(0);
  /** Whether the auto-chain is still running. */
  const [isAutoChaining, setIsAutoChaining] = useState(false);
  /** Aborts an in-flight chain iteration (set synchronously on reset). */
  const autoChainAbortRef = useRef(false);
  /** The searchFilter the running chain belongs to. */
  const chainFilterRef = useRef<string | undefined>(undefined);

  // --- On-demand extension state -------------------------------------------
  /**
   * Guard against concurrent extension requests. A ref (not the state flag
   * below) because two clicks landing in the same tick share one render, so
   * only a synchronously-mutated value can block the second.
   */
  const isExtendingRef = useRef(false);
  /** Mirrors {@link isExtendingRef} for rendering (disables the pager). */
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  /**
   * Generation an in-flight extension belongs to, bumped on every reset.
   *
   * Ownership token for {@link isExtendingRef}: without it a stale extension
   * that resolves after a filter change would release the guard belonging to a
   * newer extension still in flight, letting the next click re-send the same
   * cursor and append duplicate rows.
   */
  const extensionGenerationRef = useRef(0);

  // --- Pager state (client-side only) ---
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  const {
    data: browseData,
    loading: browseLoading,
    error: browseError,
    fetchMore: browseFetchMore,
  } = useListNamespacesQuery({
    // Every variable is declared explicitly (including the null nextToken) so
    // the request shape is stable and matches mocks exactly under
    // MockedProvider/WildcardMockLink, which compare variables by equality.
    variables: {
      namespaceIdPrefix: searchFilter || undefined,
      pageSize: NAMESPACE_FETCH_BATCH_SIZE,
      nextToken: null,
    },
    // Bypass the Apollo cache entirely — namespaces are accumulated in local
    // state (allNamespaceIds) and must always be fresh. The fetchMore calls
    // below supply the `updateQuery` that 'no-cache' requires (Apollo error 29).
    fetchPolicy: 'no-cache',
  });

  // --- Reset everything when the search filter changes -----------------------
  // Declared FIRST on purpose. Effects run in declaration order within a
  // commit, and `autoChainAbortRef` is a ref (mutated synchronously), so the
  // chain effect below observes the abort in the very same commit as the filter
  // change and refuses to issue a request that mixes the old cursor with the
  // new prefix.
  useEffect(() => {
    autoChainAbortRef.current = true;
    setIsAutoChaining(false);
    setAllNamespaceIds([]);
    setBrowseToken(null);
    setBrowseInitialized(false);
    setBatchesFetched(0);
    setCurrentPageIndex(1);
    // Clear any in-flight extension too: its response is discarded by the
    // guards in `goToPage`, but without this the pager would stay disabled for
    // good if the filter changed while an extension was outstanding.
    // Bumping the generation disowns that extension, so when it eventually
    // resolves it cannot release the guard of the extension started after this
    // reset.
    extensionGenerationRef.current += 1;
    isExtendingRef.current = false;
    setIsLoadingMore(false);
  }, [searchFilter]);

  // --- Seed from the first response and start the auto-chain ----------------
  // The `!browseLoading` gate matters: while the display query is refetching
  // for a new prefix, Apollo can still hand back the previous response object.
  // Seeding from it would populate the new context with the old filter's
  // namespaces and then latch `browseInitialized`, so the real response would
  // be dropped.
  useEffect(() => {
    if (browseLoading || !browseData?.listNamespaces || browseInitialized) {
      return;
    }

    const ids = browseData.listNamespaces.namespaces.map(
      (ns) => ns.namespaceId,
    );
    const nextToken = browseData.listNamespaces.nextToken ?? null;

    setAllNamespaceIds(ids);
    setBrowseToken(nextToken);
    setBrowseInitialized(true);
    setBatchesFetched(1);

    if (nextToken) {
      setIsAutoChaining(true);
      autoChainAbortRef.current = false;
      chainFilterRef.current = searchFilter;
    }
  }, [browseData, browseLoading, browseInitialized, searchFilter]);

  // --- Auto-chain: append the remaining batches -----------------------------
  useEffect(() => {
    if (!isAutoChaining || !browseInitialized) return;

    if (!browseToken || batchesFetched >= NAMESPACE_INITIAL_BATCHES) {
      // Chain complete — either the server is exhausted or the cap is reached.
      setIsAutoChaining(false);
      return;
    }

    if (autoChainAbortRef.current) {
      // The filter changed in this same commit; the cursor we hold belongs to
      // the previous prefix and must not be sent.
      setIsAutoChaining(false);
      return;
    }

    let cancelled = false;

    const fetchNextBatch = async () => {
      try {
        const result = await browseFetchMore({
          variables: { nextToken: browseToken },
          // Required by Apollo when fetchPolicy is 'no-cache' (error 29).
          // Batches are accumulated in local state, so this merge is an
          // identity — only fetchMore's return value is consumed.
          updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
        });

        if (cancelled || autoChainAbortRef.current) return;

        // A filter change may have already restarted the chain and cleared the
        // abort flag; verify this chain still owns the current context.
        if (chainFilterRef.current !== searchFilter) return;

        const newIds = result.data.listNamespaces.namespaces.map(
          (ns) => ns.namespaceId,
        );
        const newToken = result.data.listNamespaces.nextToken ?? null;

        setAllNamespaceIds((prev) => [...prev, ...newIds]);
        setBrowseToken(newToken);
        setBatchesFetched((prev) => prev + 1);

        if (!newToken) {
          setIsAutoChaining(false);
        }
      } catch (chainError) {
        // Stop the chain gracefully and keep whatever was already loaded.
        /* istanbul ignore next -- untestable: Apollo's fetchMore resolves with { error } rather than rejecting; a genuine rejection needs a network-level failure WildcardMockLink cannot simulate */
        console.error(
          '[useNamespaceList] Auto-chain fetch failed:',
          chainError,
        );
        /* istanbul ignore next */
        setIsAutoChaining(false);
      }
    };

    fetchNextBatch();

    return () => {
      cancelled = true;
    };
  }, [
    isAutoChaining,
    browseInitialized,
    browseToken,
    batchesFetched,
    browseFetchMore,
    searchFilter,
  ]);

  // --- Client-side pagination over the accumulated array --------------------
  const pagesCount = Math.max(
    1,
    Math.ceil(allNamespaceIds.length / NAMESPACE_UI_PAGE_SIZE),
  );
  const pageStart = (currentPageIndex - 1) * NAMESPACE_UI_PAGE_SIZE;
  const namespaceIds = allNamespaceIds.slice(
    pageStart,
    pageStart + NAMESPACE_UI_PAGE_SIZE,
  );

  /**
   * Moves the pager to `page`, extending the loaded range first when needed.
   *
   * Within the already-loaded pages this is a pure slice-index change and
   * issues no request — the common path. A request beyond the loaded frontier
   * follows the server's cursors, batch after batch, until the accumulated
   * namespaces actually MATERIALISE `page`: a single batch is not enough to
   * assume it does, because `listNamespaces` applies the VIEW permission filter
   * after paging and can hand back an empty or undersized batch that still
   * carries another cursor.
   *
   * The page index therefore advances only once that page exists, and not at
   * all when the server is exhausted first (the pager then stays where it is).
   * Both guarantees together are what keep the pager from ever rendering an
   * empty page and keep `currentPage <= pagesCount` true at all times.
   */
  const goToPage = useCallback(
    async (page: number) => {
      if (page <= pagesCount) {
        setCurrentPageIndex(page);
        return;
      }

      // Beyond the loaded frontier: extend on demand. Bail out when another
      // extension is already running (the ref is mutated synchronously, so two
      // clicks in one tick cannot both pass), when the server is exhausted, or
      // while the mount-time chain still owns the cursor.
      if (isExtendingRef.current || !browseToken || isAutoChaining) {
        return;
      }

      isExtendingRef.current = true;
      setIsLoadingMore(true);
      // This extension owns the guard only while the generation is unchanged;
      // a reset (filter change) bumps it and hands ownership to whatever runs
      // next.
      const myGeneration = extensionGenerationRef.current;

      try {
        // Both counters are tracked LOCALLY across iterations: the setters
        // below are async, so `allNamespaceIds` / `browseToken` would still
        // hold the pre-loop values when the next iteration read them.
        let loadedCount = allNamespaceIds.length;
        let cursor: string | null = browseToken;
        // Set once `loadedCount` covers `page`. No hard iteration bound is
        // needed (nor would one be honest, since the namespace list has no
        // upper limit): the walk terminates naturally when the requested page
        // materialises or the server stops handing back a cursor.
        let pageMaterialised = false;

        while (cursor) {
          // Annotated explicitly: inside the loop the cursor is derived from
          // the previous response, and without a type here TypeScript sees
          // `result` as referenced by its own initializer and falls back to
          // `any` (TS7022).
          const result: ApolloQueryResult<ListNamespacesQuery> =
            await browseFetchMore({
              variables: { nextToken: cursor },
              // Required by Apollo when fetchPolicy is 'no-cache' (error 29).
              // Batches are accumulated in local state, so this merge is an
              // identity — only fetchMore's return value is consumed.
              updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
            });

          // Re-checked on EVERY iteration, not just the first: the filter can
          // change at any point during the walk. The response then belongs to
          // the previous prefix — discard it, stop walking, and leave both the
          // accumulated array and the page index alone.
          if (autoChainAbortRef.current) return;
          if (chainFilterRef.current !== searchFilter) return;
          // Ownership can also be lost without the two flags above catching it
          // (a filter change and back again clears the abort and restores the
          // prefix). The generation is the authoritative token.
          if (extensionGenerationRef.current !== myGeneration) return;

          const newIds = result.data.listNamespaces.namespaces.map(
            (ns) => ns.namespaceId,
          );
          const newToken = result.data.listNamespaces.nextToken ?? null;

          setAllNamespaceIds((prev) => [...prev, ...newIds]);
          setBrowseToken(newToken);
          setBatchesFetched((prev) => prev + 1);

          loadedCount += newIds.length;
          cursor = newToken;

          if (Math.ceil(loadedCount / NAMESPACE_UI_PAGE_SIZE) >= page) {
            pageMaterialised = true;
            break;
          }
        }

        // Advance only once the batches committed above actually contain the
        // requested page. When the server ran out first, everything fetched is
        // kept but the pager stays on its current page.
        if (pageMaterialised) {
          setCurrentPageIndex(page);
        }
      } catch (extendError) {
        // Keep whatever is already loaded and stay on the current page.
        /* istanbul ignore next -- untestable: Apollo's fetchMore resolves with { error } rather than rejecting; a genuine rejection needs a network-level failure WildcardMockLink cannot simulate */
        console.error('[useNamespaceList] Extend fetch failed:', extendError);
      } finally {
        // Release the guard only when this extension still owns it. Skipping
        // the release for a disowned (stale) extension is safe: the reset that
        // bumped the generation already cleared both the ref and the flag, so
        // the only live owner is the newer extension, which must keep the pager
        // disabled until its own response lands.
        if (extensionGenerationRef.current === myGeneration) {
          isExtendingRef.current = false;
          setIsLoadingMore(false);
        }
      }
    },
    [
      pagesCount,
      allNamespaceIds.length,
      browseToken,
      isAutoChaining,
      browseFetchMore,
      searchFilter,
    ],
  );

  return {
    namespaceIds,
    currentPage: currentPageIndex,
    pagesCount,
    // Only meaningful once the chain has settled: a surviving cursor means the
    // server holds namespaces beyond the batches loaded here.
    hasMore: !isAutoChaining && !!browseToken,
    loading: browseLoading && allNamespaceIds.length === 0,
    isLoadingMore,
    error: browseError,
    totalLoaded: namespaceIds.length,
    goToPage,
  };
}
